import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import CardMedia from "@mui/material/CardMedia";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PageviewIcon from "@mui/icons-material/Pageview";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

import { budgetOptions, MANUFACTURER_SERVICES } from "utils/data";
import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Switch from "@mui/material/Switch";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";

import UTreeSelect from "components/UpStyled/UTreeSelect";

import { filterParser, sortParser } from "utils/helper";
import { handleUploadFiles, getStyleImages, getTechPackImages } from "utils/imageHelper";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import Avatar from "@mui/material/Avatar";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Error404 from "pages/error/404";

import AddResourceModal from "pages/online-resource/components/AddResourceModal";

const AdminOnlineResource = () => {
  const [view, setView] = useState("table"); //grid,table
  const [resourceList, setResourceList] = useState([]);

  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  let location = useLocation();
  const navigate = useNavigate();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);

  const account = useContext(AccountContext);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    if (account.id) {
      handleRefresh();
    }
  }, [account.id]);

  const handleRefresh = async () => {
    try {
      const { data } = await api.onlineResource.list({ order: "-created_at" });

      setResourceList(data?.online_resources || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleDeleteResource = async () => {
    try {
      await api.onlineResource.delete(selectedResource.id);
      handleRefresh();
      setDeleteVisible(false);
      toast.success(`Delete resource ${selectedResource?.title} successfully!`);
    } catch (err) {
      toast.error(`Failed to delete resource ${selectedResource?.title}!`);

      console.log(err);
    }
  };

  const handleViewChange = (e, value) => {
    if (value !== null) {
      setView(value);
    }
  };

  const getResourceTableRow = (item) => {
    return {
      key: item.id,
      image: item?.img || null,
      title: item.title,
      type: item?.type,
      category: item?.category,
      created_at: item?.created_at,
      action: item,
    };
  };

  const columns = [
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <ImageCardWrapper data={value} mode={"normal"} height={90} width={90} iconMode />;
      },
    },

    {
      Header: "Title",
      accessor: "title",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 300 }}>{value}</div>;
      },
    },
    {
      Header: "Type",
      accessor: "type",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 300 }}>{value}</div>;
      },
    },
    {
      Header: "Category",
      accessor: "category",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word" }}>{value}</div>;
      },
    },
    {
      Header: "Created Date",
      accessor: "created_at",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="Edit Resource">
            <EditIcon
              fontSize={"small"}
              color="dark"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedResource(value);
                setEditVisible(true);
              }}
            />
          </Tooltip>
          |
          <Tooltip title="Remove Resource">
            <DeleteForeverIcon
              fontSize={"small"}
              color="error"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedResource(value);
                setDeleteVisible(true);
              }}
            />
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const rows = resourceList.map((item) => getResourceTableRow(item));

  return (
    <SuiBox my={1}>
      <Card sx={{ p: 3 }}>
        <SuiBox display="flex" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
          <SuiButton
            variant="gradient"
            color="dark"
            onClick={() => setCreateVisible(true)}
            size="small"
          >
            +Add Resource
          </SuiButton>
        </SuiBox>

        <DataTable
          table={{ columns, rows: rows }}
          entriesPerPage={{ defaultValue: 20 }}
          noEndBorder
        />
      </Card>

      {createVisible ? (
        <AddResourceModal
          visible={createVisible}
          handleClose={() => setCreateVisible(false)}
          handleRefresh={handleRefresh}
        />
      ) : null}
      {editVisible ? (
        <AddResourceModal
          visible={editVisible}
          handleClose={() => setEditVisible(false)}
          isEdit={true}
          resource={selectedResource}
          handleRefresh={handleRefresh}
        />
      ) : null}

      {deleteVisible && selectedResource ? (
        <Prompt
          heading="Delete Resource"
          text={`Are you sure you want to delete: ${selectedResource.title}`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteResource(selectedResource)}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default AdminOnlineResource;
